import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const API = "/backend/public/api";

const shellStyle = {
  background: "linear-gradient(135deg, #eef6ff 0%, #ffffff 45%, #fff7ed 100%)",
  minHeight: "calc(100vh - 140px)",
  padding: "42px 16px",
};

const panelStyle = {
  maxWidth: "1080px",
  margin: "0 auto",
  display: "grid",
  gap: "18px",
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: "14px",
  boxShadow: "0 18px 42px rgba(15, 23, 42, 0.08)",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: "10px",
  padding: "12px 14px",
  color: "#0f172a",
  outline: "none",
};

const thStyle = {
  padding: "12px",
  color: "#7c2d3a",
  background: "#fff7ed",
  borderBottom: "1px solid #e2e8f0",
  fontSize: "13px",
  textAlign: "left",
};

const tdStyle = {
  padding: "12px",
  borderBottom: "1px solid #e2e8f0",
  color: "#0f172a",
  fontSize: "13px",
};

function formatMarks(obtained, full) {
  return `${Number(obtained || 0).toFixed(0)} / ${Number(full || 0).toFixed(0)}`;
}

function toDateInputValue(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const match = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(value);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;

  return "";
}

function ResultVerification() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    registration_no: searchParams.get("reg") || searchParams.get("registration_no") || "",
    dob: toDateInputValue(searchParams.get("dob") || ""),
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const isAutoLink = useMemo(() => Boolean(searchParams.get("reg") || searchParams.get("registration_no")), [searchParams]);

  const fetchResult = async (nextForm = form) => {
    if (!nextForm.registration_no || !nextForm.dob) {
      setMessage("Registration number and date of birth are required.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      const res = await axios.get(`${API}/result-verification`, {
        params: {
          registration_no: nextForm.registration_no,
          dob: nextForm.dob,
        },
      });
      setResult(res.data);
    } catch (error) {
      setResult(null);
      setMessage(error.response?.data?.message || "Result verification failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (form.registration_no && form.dob) {
      fetchResult(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    fetchResult();
  };

  const student = result?.student || {};
  const summary = result?.summary || {};
  const rows = Array.isArray(result?.rows) ? result.rows : [];

  return (
    <div style={shellStyle}>
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #result-print, #result-print * { visibility: visible; }
            #result-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
            .no-print { display: none !important; }
          }
        `}
      </style>
      <div style={panelStyle}>
        <section style={{ ...cardStyle, padding: "22px" }} className="no-print">
          <p style={{ margin: 0, color: "#7c2d3a", fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Official Verification
          </p>
          <h1 style={{ margin: "8px 0 8px", color: "#0f172a", fontSize: "32px" }}>Result Verification</h1>
          <p style={{ margin: 0, color: "#64748b" }}>
            QR scan korle registration number and date of birth auto-fill hoye result ekhanei show hobe.
          </p>
        </section>

        <section style={{ ...cardStyle, padding: "18px" }} className="no-print">
          <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr)) auto", gap: "12px", alignItems: "end" }}>
            <div>
              <label style={{ color: "#475569", fontSize: "13px", fontWeight: 800, marginBottom: "8px", display: "block" }}>Registration Number</label>
              <input
                style={inputStyle}
                value={form.registration_no}
                onChange={(event) => setForm((prev) => ({ ...prev, registration_no: event.target.value }))}
                placeholder="Enter registration number"
              />
            </div>
            <div>
              <label style={{ color: "#475569", fontSize: "13px", fontWeight: 800, marginBottom: "8px", display: "block" }}>Date of Birth</label>
              <input
                type="date"
                style={inputStyle}
                value={form.dob}
                onChange={(event) => setForm((prev) => ({ ...prev, dob: event.target.value }))}
              />
            </div>
            <button type="submit" className="btn btn-success" disabled={loading} style={{ minHeight: "48px" }}>
              {loading ? "Checking..." : "Check Result"}
            </button>
          </form>
          {isAutoLink ? <div style={{ marginTop: "10px", color: "#166534", fontSize: "13px", fontWeight: 700 }}>QR details loaded automatically.</div> : null}
          {message ? <div style={{ marginTop: "10px", color: "#b91c1c", fontWeight: 700 }}>{message}</div> : null}
        </section>

        {result ? (
          <section id="result-print" style={{ ...cardStyle, overflow: "hidden" }}>
            <div style={{ background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)", color: "#fff", padding: "22px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <p style={{ margin: 0, fontSize: "12px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.78 }}>Verified Result</p>
                  <h2 style={{ margin: "8px 0 0", fontSize: "28px" }}>{student.student_name || "Student"}</h2>
                </div>
                <button type="button" className="btn btn-light no-print" onClick={() => window.print()}>
                  <i className="bi bi-printer me-2"></i>
                  Print
                </button>
              </div>
            </div>

            <div style={{ padding: "22px", display: "grid", gap: "18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "18px", alignItems: "start" }}>
                <div style={{ width: "120px", height: "145px", border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden", background: "#f8fafc" }}>
                  {student.photo_url ? <img src={student.photo_url} alt={student.student_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" }}>
                  {[
                    ["Registration No", student.registration_no],
                    ["Session", student.session_year],
                    ["Course", student.course_name],
                    ["Study Centre", student.study_centre_name],
                    ["Total Result", `${summary.total_obtained} / ${summary.total_full}`],
                    ["Percentage", `${summary.percentage}%`],
                    ["Grade", summary.grade],
                    ["Verified At", result.verified_at],
                  ].map(([label, value]) => (
                    <div key={label} style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px", background: "#f8fafc" }}>
                      <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 800 }}>{label}</div>
                      <div style={{ color: "#0f172a", fontWeight: 800, marginTop: "4px" }}>{value || "-"}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px" }}>
                {[
                  ["Theory", summary.theory_obtained, summary.theory_full],
                  ["Practical", summary.practical_obtained, summary.practical_full],
                  ["Project", summary.project_obtained, summary.project_full],
                  ["Viva", summary.viva_obtained, summary.viva_full],
                ].map(([label, obtained, full]) => (
                  <div key={label} style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px" }}>
                    <div style={{ color: "#7c2d3a", fontWeight: 900 }}>{label}</div>
                    <div style={{ color: "#0f172a", fontSize: "22px", fontWeight: 900 }}>{formatMarks(obtained, full)}</div>
                  </div>
                ))}
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Module</th>
                      <th style={thStyle}>Theory</th>
                      <th style={thStyle}>Practical</th>
                      <th style={thStyle}>Project</th>
                      <th style={thStyle}>Viva</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={`${row.module_title}-${index}`}>
                        <td style={tdStyle}>{row.module_title}</td>
                        <td style={tdStyle}>{formatMarks(row.theory?.obtained, row.theory?.full)}</td>
                        <td style={tdStyle}>{formatMarks(row.practical?.obtained, row.practical?.full)}</td>
                        <td style={tdStyle}>{formatMarks(row.project?.obtained, row.project?.full)}</td>
                        <td style={tdStyle}>{formatMarks(row.viva?.obtained, row.viva?.full)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

export default ResultVerification;



